import { useState } from "react";
import styles from "../styles/theme";
import { ALL_CERT_OPTIONS, UXO_ROLE_OPTIONS } from "../utils/constants";

const JOB_TITLE_OPTIONS = [...UXO_ROLE_OPTIONS, 'Other'];

const UXO_POSITION_CERTS = [
  "UXO Technician I", "UXO Technician II", "UXO Technician III",
  "UXO Quality Control", "UXO Safety Officer / UXOSO",
  "SUXOS - Senior UXO Supervisor",
];

export default function PostJob({
  jobPost, setJobPost, postStep, setPostStep, maxPostStep,
  submitError, paymentLoading, onSubmit, onBack, isPaid,
}) {
  const [titleIsOther, setTitleIsOther] = useState(!UXO_ROLE_OPTIONS.includes(jobPost.title) && jobPost.title !== '');
  const [step1Errors, setStep1Errors] = useState({});
  const [step2Error, setStep2Error] = useState('');

  const toggleJobCert = (cert, type) => {
    setJobPost(p => {
      const field = type === 'required' ? 'requiredCerts' : 'preferredCerts';
      const other = type === 'required' ? 'preferredCerts' : 'requiredCerts';
      return {
        ...p,
        [field]: p[field].includes(cert) ? p[field].filter(c => c !== cert) : [...p[field], cert],
        [other]: p[other].filter(c => c !== cert),
      };
    });
    setStep2Error('');
  };

  const validateStep1 = () => {
    const errors = {};
    if (!jobPost.title.trim()) errors.title = 'Job title is required';
    if (!jobPost.location.trim()) errors.location = 'Location is required';
    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const hasUxoRole = UXO_POSITION_CERTS.some(c => jobPost.requiredCerts.includes(c));
    if (!hasUxoRole) {
      setStep2Error('At least one UXO position must be marked as Required');
      return false;
    }
    setStep2Error('');
    return true;
  };

  return (
    <div style={styles.formWrap} data-form-wrap>
      <button style={styles.backBtn} data-back-btn onClick={onBack}>{"\u2190"} Back</button>
      <div style={styles.formCard} data-form-card>
        <div style={styles.formSteps}>
          {[1, 2, 3].map(s => {
            const isCompleted = s < postStep;
            const isCurrent = s === postStep;
            const isVisited = s <= (maxPostStep || postStep);
            const isClickable = isVisited && !isCurrent;
            return (
              <div key={s}
                onClick={isClickable ? () => setPostStep(s) : undefined}
                style={{
                  ...styles.step,
                  ...(isCurrent ? styles.stepActive : {}),
                  ...(isCompleted ? { background: '#1a4a2e', color: '#4ade80', border: '1px solid #4ade8033' } : {}),
                  ...(isClickable ? { cursor: 'pointer' } : {}),
                  ...(!isVisited ? { opacity: 0.4 } : {}),
                }}>
                {isCompleted ? "\u2713" : s}
              </div>
            );
          })}
        </div>
        <h2 style={styles.formTitle} data-form-title>
          {postStep === 1 && "Company & Role Details"}
          {postStep === 2 && "Requirements"}
          {postStep === 3 && (isPaid ? "Review & Publish" : "Review & Payment")}
        </h2>

        {postStep === 1 && (
          <div style={styles.formFields} data-form-fields>
            <label style={styles.label}>Company Name</label>
            <input style={styles.input} placeholder="Your company name" value={jobPost.company} onChange={e => setJobPost(p => ({ ...p, company: e.target.value }))} />
            <label style={styles.label}>Job Title</label>
            <select style={styles.input} value={titleIsOther ? 'Other' : (UXO_ROLE_OPTIONS.includes(jobPost.title) ? jobPost.title : '')}
              onChange={e => {
                const val = e.target.value;
                if (val === 'Other') {
                  setTitleIsOther(true);
                  setJobPost(p => ({ ...p, title: '' }));
                } else {
                  setTitleIsOther(false);
                  setJobPost(p => ({ ...p, title: val }));
                }
                setStep1Errors(prev => ({ ...prev, title: undefined }));
              }}>
              <option value="" disabled>Select a role...</option>
              {JOB_TITLE_OPTIONS.map(role => <option key={role} value={role}>{role}</option>)}
            </select>
            {titleIsOther && (
              <input style={{ ...styles.input, marginTop: 8 }} placeholder="Enter custom job title"
                value={jobPost.title} onChange={e => { setJobPost(p => ({ ...p, title: e.target.value })); setStep1Errors(prev => ({ ...prev, title: undefined })); }} />
            )}
            {step1Errors.title && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {step1Errors.title}</div>}
            <label style={styles.label}>Location</label>
            <input style={styles.input} placeholder="City, State or Remote/Travel" value={jobPost.location}
              onChange={e => { setJobPost(p => ({ ...p, location: e.target.value })); setStep1Errors(prev => ({ ...prev, location: undefined })); }} />
            {step1Errors.location && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {step1Errors.location}</div>}
            <label style={styles.label}>Employment Type</label>
            <select style={styles.input} value={jobPost.type} onChange={e => setJobPost(p => ({ ...p, type: e.target.value }))}>
              <option>Contract</option>
              <option>Full-Time</option>
              <option>Part-Time</option>
              <option>Temporary</option>
            </select>
            <label style={styles.label}>Salary / Pay Rate</label>
            <input style={styles.input} placeholder="e.g. $45–$55/hr" value={jobPost.salary} onChange={e => setJobPost(p => ({ ...p, salary: e.target.value }))} />
            <label style={styles.label}>Job Description</label>
            <textarea style={{ ...styles.textarea, minHeight: "120px" }} placeholder="Describe the role, site conditions, project duration..." value={jobPost.description} onChange={e => setJobPost(p => ({ ...p, description: e.target.value }))} />
            <button style={styles.btnPrimary} data-btn-primary onClick={() => { if (validateStep1()) setPostStep(2); }}>Next {"\u2192"}</button>
          </div>
        )}

        {postStep === 2 && (
          <div style={styles.formFields} data-form-fields>
            <p style={styles.certInstructions}>For each qualification, select Required or Preferred — or leave unselected if not relevant.</p>
            {ALL_CERT_OPTIONS.map(cert => (
              <div key={cert} style={styles.certRequireRow} data-cert-require-row>
                <span style={styles.certRequireLabel}>{cert}</span>
                <div style={styles.yesNoRow}>
                  <button style={{ ...styles.reqBtn, ...(jobPost.requiredCerts.includes(cert) ? styles.reqBtnRequired : {}) }} onClick={() => toggleJobCert(cert, 'required')}>Required</button>
                  <button style={{ ...styles.reqBtn, ...(jobPost.preferredCerts.includes(cert) ? styles.reqBtnPreferred : {}) }} onClick={() => toggleJobCert(cert, 'preferred')}>Preferred</button>
                </div>
              </div>
            ))}
            {step2Error && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {step2Error}</div>}
            <div style={styles.formRow} data-form-row>
              <button style={styles.btnSecondary} data-btn-secondary onClick={() => setPostStep(1)}>{"\u2190"} Back</button>
              <button style={styles.btnPrimary} data-btn-primary onClick={() => { if (validateStep2()) setPostStep(3); }}>Next {"\u2192"}</button>
            </div>
          </div>
        )}

        {postStep === 3 && (
          <div style={styles.formFields} data-form-fields>
            <div style={styles.reviewCard}>
              {[
                ["Company", jobPost.company],
                ["Title", jobPost.title],
                ["Location", jobPost.location],
                ["Type", jobPost.type],
                ["Pay", jobPost.salary],
                ["Required Certs", jobPost.requiredCerts.join(", ") || "None"],
                ["Preferred Certs", jobPost.preferredCerts.join(", ") || "None"],
              ].map(([label, val]) => (
                <div key={label} style={styles.reviewRow} data-review-row>
                  <span style={styles.reviewLabel}>{label}</span>
                  <span>{val || "\u2014"}</span>
                </div>
              ))}
            </div>
            {!isPaid && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                {/* Starter Tier */}
                <div style={{ ...styles.pricingBox, flex: '1 1 200px', marginBottom: 0 }} data-pricing-box>
                  <div style={styles.pricingTitle}>Starter</div>
                  <div style={styles.pricingAmount} data-pricing-amount>$1,199 <span style={styles.pricingPer}>/ month</span></div>
                  <div style={{ color: '#7a7570', fontSize: 12, marginBottom: 8 }}>or $11,990/year</div>
                  <ul style={styles.pricingFeatures}>
                    <li>{"\u2713"} 5 active job posts</li>
                    <li>{"\u2713"} 10 unlocks/month (rollover max 15)</li>
                    <li>{"\u2713"} Email support (2 business days)</li>
                  </ul>
                </div>
                {/* Pro Tier */}
                <div style={{ ...styles.pricingBox, flex: '1 1 200px', marginBottom: 0, border: '1px solid #d97706' }} data-pricing-box>
                  <div style={styles.pricingTitle}>Pro</div>
                  <div style={styles.pricingAmount} data-pricing-amount>$1,999 <span style={styles.pricingPer}>/ month</span></div>
                  <div style={{ color: '#7a7570', fontSize: 12, marginBottom: 8 }}>or $19,990/year</div>
                  <ul style={styles.pricingFeatures}>
                    <li>{"\u2713"} Unlimited job posts</li>
                    <li>{"\u2713"} 15 unlocks/month (rollover max 25)</li>
                    <li>{"\u2713"} Priority email support (1 business day)</li>
                    <li>{"\u2713"} Monthly account check-in</li>
                  </ul>
                </div>
              </div>
            )}
            {isPaid && (
              <div style={{ background: '#0a2a1a', border: '1px solid #4ade8033', borderRadius: 8, padding: '14px 18px', marginBottom: 8, fontSize: 14, color: '#4ade80' }}>
                {"\u2713"} Paid employer account — no payment required
              </div>
            )}
            <div style={styles.formRow} data-form-row>
              <button style={styles.btnSecondary} data-btn-secondary onClick={() => setPostStep(2)}>{"\u2190"} Back</button>
              <button style={styles.btnPrimary} data-btn-primary onClick={onSubmit} disabled={paymentLoading}>
                {paymentLoading ? 'Publishing...' : isPaid ? 'Publish Job \u2192' : 'Submit & Pay \u2192'}
              </button>
            </div>
            {submitError && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {submitError}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
